import { queryHtmlElement } from "../core/dom";
import { FormErrorSummary } from "../core/form-error-summary";
import type { FormErrorSummaryMessage } from "../core/form-error-summary";
import {
  normalizeGoogleMapsApiKey,
  resolveMainMapSearch
} from "./main-map-search";

/**
 * Enhances the signup page with lightweight account-creation validation.
 */
export class SignupFormController {
  private readonly errorSummary: FormErrorSummary;
  private readonly form: HTMLFormElement | null;
  private readonly emailInput: HTMLInputElement | null;
  private readonly emailError: HTMLElement | null;
  private readonly passwordInput: HTMLInputElement | null;
  private readonly passwordError: HTMLElement | null;
  private readonly confirmPasswordInput: HTMLInputElement | null;
  private readonly confirmPasswordError: HTMLElement | null;
  private readonly latitudeInput: HTMLInputElement | null;
  private readonly latitudeError: HTMLElement | null;
  private readonly longitudeInput: HTMLInputElement | null;
  private readonly longitudeError: HTMLElement | null;
  private readonly disclosureInput: HTMLInputElement | null;
  private readonly disclosureError: HTMLElement | null;
  private readonly locationSearchInput: HTMLInputElement | null;
  private readonly locationSearchButton: HTMLButtonElement | null;
  private readonly locationStatus: HTMLElement | null;
  private readonly googleMapsApiKey: string;

  public constructor(root: HTMLElement) {
    this.errorSummary = new FormErrorSummary(root);
    this.form = queryHtmlElement(root, "[data-signup-form]", HTMLFormElement);
    this.emailInput = queryHtmlElement(root, "[data-signup-email]", HTMLInputElement);
    this.emailError = queryHtmlElement(root, "[data-field-error-for='email']", HTMLElement);
    this.passwordInput = queryHtmlElement(root, "[data-signup-password]", HTMLInputElement);
    this.passwordError = queryHtmlElement(root, "[data-field-error-for='password']", HTMLElement);
    this.confirmPasswordInput = queryHtmlElement(root, "[data-signup-confirm-password]", HTMLInputElement);
    this.confirmPasswordError = queryHtmlElement(
      root,
      "[data-field-error-for='confirm-password']",
      HTMLElement
    );
    this.latitudeInput = queryHtmlElement(root, "[data-signup-latitude]", HTMLInputElement);
    this.latitudeError = queryHtmlElement(root, "[data-field-error-for='latitude']", HTMLElement);
    this.longitudeInput = queryHtmlElement(root, "[data-signup-longitude]", HTMLInputElement);
    this.longitudeError = queryHtmlElement(root, "[data-field-error-for='longitude']", HTMLElement);
    this.disclosureInput = queryHtmlElement(root, "[data-signup-disclosure]", HTMLInputElement);
    this.disclosureError = queryHtmlElement(root, "[data-field-error-for='disclosure']", HTMLElement);
    this.locationSearchInput = queryHtmlElement(
      root,
      "[data-signup-location-search]",
      HTMLInputElement
    );
    this.locationSearchButton = queryHtmlElement(
      root,
      "[data-signup-location-search-button]",
      HTMLButtonElement
    );
    this.locationStatus = queryHtmlElement(root, "[data-signup-location-status]", HTMLElement);
    this.googleMapsApiKey = normalizeGoogleMapsApiKey(root.dataset.googleMapsApiKey);
  }

  public connect(): void {
    if (this.form !== null) {
      this.form.addEventListener("submit", this.handleSubmit);
    }
    this.locationSearchButton?.addEventListener("click", this.handleLocationSearch);
    this.locationSearchInput?.addEventListener("keydown", this.handleLocationSearchKeydown);

    if (this.errorSummary.hasVisibleContent()) {
      this.errorSummary.focus();
    }
  }

  private readonly handleSubmit = (event: SubmitEvent): void => {
    const errors: FormErrorSummaryMessage[] = [];
    this.errorSummary.clear();
    this.form?.classList.add("was-validated");

    if (this.emailInput !== null) {
      this.emailInput.value = this.emailInput.value.trim();
      if (!this.emailInput.checkValidity()) {
        errors.push(
          this.showFieldError(
            this.emailInput,
            this.emailError,
            "Enter a valid email address."
          )
        );
      } else {
        this.clearFieldError(this.emailInput, this.emailError);
      }
    }

    if (this.passwordInput !== null) {
      if (!this.passwordInput.checkValidity()) {
        errors.push(
          this.showFieldError(
            this.passwordInput,
            this.passwordError,
            "Password must be at least 8 characters."
          )
        );
      } else {
        this.clearFieldError(this.passwordInput, this.passwordError);
      }
    }

    if (this.confirmPasswordInput !== null && this.passwordInput !== null) {
      if (this.confirmPasswordInput.value !== this.passwordInput.value) {
        errors.push(
          this.showFieldError(
            this.confirmPasswordInput,
            this.confirmPasswordError,
            "Password and confirmation must match."
          )
        );
      } else if (!this.confirmPasswordInput.checkValidity()) {
        errors.push(
          this.showFieldError(
            this.confirmPasswordInput,
            this.confirmPasswordError,
            "Confirm your password."
          )
        );
      } else {
        this.clearFieldError(this.confirmPasswordInput, this.confirmPasswordError);
      }
    }

    const rawLatitude = this.latitudeInput?.value.trim() ?? "";
    const rawLongitude = this.longitudeInput?.value.trim() ?? "";
    const latitude = Number(rawLatitude);
    const longitude = Number(rawLongitude);
    const latitudeInvalid =
      rawLatitude.length === 0 ||
      !Number.isFinite(latitude) ||
      latitude < -90 ||
      latitude > 90;
    const longitudeInvalid =
      rawLongitude.length === 0 ||
      !Number.isFinite(longitude) ||
      longitude < -180 ||
      longitude > 180;
    if (
      this.latitudeInput !== null &&
      this.longitudeInput !== null &&
      (latitudeInvalid || longitudeInvalid)
    ) {
      if (latitudeInvalid) {
        const message = "Enter a valid planning-area latitude.";
        errors.push(this.showFieldError(this.latitudeInput, this.latitudeError, message));
      } else {
        this.clearFieldError(this.latitudeInput, this.latitudeError);
      }
      if (longitudeInvalid) {
        const message = "Enter a valid planning-area longitude.";
        errors.push(this.showFieldError(this.longitudeInput, this.longitudeError, message));
      } else {
        this.clearFieldError(this.longitudeInput, this.longitudeError);
      }
    } else {
      this.clearFieldError(this.latitudeInput, this.latitudeError);
      this.clearFieldError(this.longitudeInput, this.longitudeError);
    }

    if (this.disclosureInput !== null && !this.disclosureInput.checkValidity()) {
      errors.push(
        this.showFieldError(
          this.disclosureInput,
          this.disclosureError,
          "Review and acknowledge the account and billing disclosures."
        )
      );
    } else {
      this.clearFieldError(this.disclosureInput, this.disclosureError);
    }

    if (errors.length > 0) {
      event.preventDefault();
      this.errorSummary.show(errors);
      this.errorSummary.focus();
    }
  };

  private readonly handleLocationSearch = (): void => {
    void this.resolveLocationSearch();
  };

  private readonly handleLocationSearchKeydown = (event: KeyboardEvent): void => {
    if (event.key === "Enter") {
      event.preventDefault();
      void this.resolveLocationSearch();
    }
  };

  private async resolveLocationSearch(): Promise<void> {
    const searchTerm = this.locationSearchInput?.value ?? "";
    this.setLocationStatus("Searching.");
    try {
      const result = await resolveMainMapSearch(searchTerm, this.googleMapsApiKey);
      this.applyLocation(result.point.latitude, result.point.longitude, result.label);
    } catch (error) {
      this.setLocationStatus(error instanceof Error ? error.message : "Location search failed.");
    }
  }

  private applyLocation(latitude: number, longitude: number, label: string): void {
    if (this.latitudeInput !== null) {
      this.latitudeInput.value = latitude.toFixed(6);
    }
    if (this.longitudeInput !== null) {
      this.longitudeInput.value = longitude.toFixed(6);
    }
    this.clearFieldError(this.latitudeInput, this.latitudeError);
    this.clearFieldError(this.longitudeInput, this.longitudeError);
    this.errorSummary.removeLinks(["#signup-planning-latitude", "#signup-planning-longitude"]);
    this.setLocationStatus(`Using ${label}.`);
  }

  private setLocationStatus(message: string): void {
    if (this.locationStatus !== null) {
      this.locationStatus.textContent = message;
    }
  }

  private clearFieldError(input: HTMLInputElement | null, errorElement: HTMLElement | null): void {
    input?.setCustomValidity("");
    input?.removeAttribute("aria-invalid");
    if (errorElement !== null) {
      errorElement.textContent = "";
    }
  }

  private showFieldError(
    input: HTMLInputElement,
    errorElement: HTMLElement | null,
    message: string
  ): FormErrorSummaryMessage {
    input.setCustomValidity(message);
    input.setAttribute("aria-invalid", "true");
    if (errorElement !== null) {
      errorElement.textContent = message;
    }
    return { href: `#${input.id}`, message };
  }
}
